import type {
  HostedLobbyPhase,
  HostedMatchType,
  HostedQueueStatePayload,
  HostedRosterPayload,
  HostedRoundEndPayload,
  HostedStartPayload,
  HostedWelcomePayload,
  LeaderLeftPayload,
  LobbyMemberPayload,
  LobbySettingsPayload,
  LobbyStartRejectedPayload,
  RoundStandingView,
} from "../networking/ClientNetwork.js";

/**
 * The hosted-lobby client state machine — pure, DOM-free, driven ONLY by
 * messages the server actually sends (`server/src/rooms/HostedArenaRoom.ts`).
 * Mirror of `quickMatchState.ts`: the screens are projections of this model;
 * nothing here touches the network or the document, which makes the whole
 * lifecycle testable in node.
 *
 * Server phase (`roster.phase` / `queueState.phase`) → client phase:
 *   lobby → "lobby" · match → "match" · post → "post"
 * Client-only phases: "idle" (nothing in flight), "connecting" (room join in
 * flight), "rejected" (welcome {rejected:'in-progress'} — the server then
 * kicks us), "error" (connect failed / connection lost).
 *
 * Leadership has THREE sources of truth and all are honoured:
 *  - `welcome.isLeader` (initial, and the no-`rejected` demotion welcome)
 *  - `roster.leaderSessionId` (any lobby/post change — NOT broadcast mid-match)
 *  - `youAreLeader` / `leaderLeft` (the only signals during a match)
 */
export type LobbyClientPhase =
  | "idle"
  | "connecting"
  | "lobby"
  | "match"
  | "post"
  | "rejected"
  | "error";

export interface LobbyModel {
  phase: LobbyClientPhase;
  /** "host" | "join" — which entry path started this attempt. */
  mode: "host" | "join";
  /** Our colyseus sessionId, set once the room join resolves. */
  ownSessionId: string;
  isLeader: boolean;
  leaderSessionId: string | null;
  members: LobbyMemberPayload[];
  settings: LobbySettingsPayload | null;
  code: string | null;
  /** True once the local Game has been booted for this room. */
  inGame: boolean;
  /** Config of the round we are in. Null between rounds — a stale config never re-boots. */
  startConfig: (LobbySettingsPayload & { endsAt: number }) | null;
  /** Roster frozen at match start (the `start` payload's copy). */
  matchRoster: LobbyMemberPayload[];
  standings: RoundStandingView[];
  roundEndReason: string;
  /**
   * LOCAL epoch ms deadlines, rebuilt from relative server fields against the
   * local clock at receipt (skew-proof — same rule as quickMatchState). The
   * hosted `roundEnd.nextStartAt` is a raw server epoch, so the post timer
   * comes from the accompanying `queueState {phase:"post", inMs}` instead.
   */
  matchEndsAt: number;
  postEndsAt: number;
  /** Last `lobbyStartRejected`, sticky until the roster changes or a start lands. */
  startRejected: LobbyStartRejectedPayload | null;
  /** Leader-migration toast text. "" = none. The screen decides display duration. */
  leaderNotice: string;
  errorMessage: string;
}

function emptyModel(): LobbyModel {
  return {
    phase: "idle",
    mode: "join",
    ownSessionId: "",
    isLeader: false,
    leaderSessionId: null,
    members: [],
    settings: null,
    code: null,
    inGame: false,
    startConfig: null,
    matchRoster: [],
    standings: [],
    roundEndReason: "",
    matchEndsAt: 0,
    postEndsAt: 0,
    startRejected: null,
    leaderNotice: "",
    errorMessage: "",
  };
}

/** Client-side copy of the server's start floor — display hints only, server gates. */
export const LOBBY_MIN_PLAYERS = 2;

export class LobbyMachine {
  private m: LobbyModel = emptyModel();

  model(): Readonly<LobbyModel> {
    return this.m;
  }

  reset(): void {
    this.m = emptyModel();
  }

  /** A host/join attempt starts — room join now in flight. */
  begin(mode: "host" | "join"): void {
    this.m = emptyModel();
    this.m.mode = mode;
    this.m.phase = "connecting";
  }

  /** Room joined (hello about to be / just sent). Still "connecting" until `welcome`. */
  connected(ownSessionId: string): void {
    this.m.ownSessionId = ownSessionId;
  }

  /**
   * `welcome` — either the accepted reply to our hello, the in-progress
   * rejection (server force-leaves us right after), or a mid-lobby demotion
   * (`{isLeader:false, phase}` with NO `rejected` field) when a preferLeader
   * hello displaces an arrival-order leader.
   */
  welcome(p: HostedWelcomePayload): void {
    if (p.rejected === "in-progress") {
      this.m.phase = "rejected";
      this.m.errorMessage = "MATCH IN PROGRESS — TRY AGAIN WHEN THE ROUND ENDS";
      return;
    }
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    this.m.isLeader = p.isLeader;
    if (p.code !== undefined) this.m.code = p.code;
    if (p.settings !== undefined) this.m.settings = p.settings;
    if (this.m.phase === "connecting") this.m.phase = "lobby";
  }

  /** `lobbyCode {code}` — sent right after an accepted welcome. */
  lobbyCode(p: { code: string }): void {
    if (p?.code) this.m.code = p.code;
  }

  /** `roster` — broadcast on every lobby/post change. Ground truth for members + settings. */
  roster(p: HostedRosterPayload): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    this.m.members = p.members ?? [];
    this.m.leaderSessionId = p.leaderSessionId ?? null;
    this.m.settings = p.settings ?? this.m.settings;
    if (p.code !== undefined) this.m.code = p.code;
    if (this.m.ownSessionId) {
      this.m.isLeader = p.leaderSessionId === this.m.ownSessionId;
    }
    this.applyServerPhase(p.phase);
    // Roster churn can only fix a rejected start (someone readied / joined /
    // left) — stop showing a stale rejection reason.
    this.m.startRejected = null;
  }

  /** `youAreLeader` — we inherited the crown (sent to the inheritor only). */
  youAreLeader(): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    this.m.isLeader = true;
    this.m.leaderSessionId = this.m.ownSessionId || this.m.leaderSessionId;
    this.reflagLeader(this.m.ownSessionId);
    this.m.leaderNotice = "YOU ARE NOW LEADER";
  }

  /** `leaderLeft {newLeaderSessionId}` — broadcast to everyone except the inheritor. */
  leaderLeft(p: LeaderLeftPayload): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    const next = p?.newLeaderSessionId ?? null;
    this.m.leaderSessionId = next;
    this.m.isLeader = next !== null && next === this.m.ownSessionId;
    this.reflagLeader(next);
    const nextName = next !== null ? this.m.members.find((x) => x.sessionId === next)?.name : undefined;
    this.m.leaderNotice = nextName ? `LEADER LEFT — ${nextName.toUpperCase()} NOW LEADS` : "LEADER LEFT";
  }

  /** `queueState {phase, inMs}` — phase flips + the skew-proof timers. */
  queueState(p: HostedQueueStatePayload, now: number = Date.now()): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    switch (p.phase) {
      case "match":
        this.m.matchEndsAt = p.inMs > 0 ? now + p.inMs : 0;
        break;
      case "post":
        this.m.postEndsAt = p.inMs > 0 ? now + p.inMs : 0;
        break;
      case "lobby":
        this.m.matchEndsAt = 0;
        this.m.postEndsAt = 0;
        break;
    }
    this.applyServerPhase(p.phase);
  }

  /** `start {roster, config}` — match start (first round OR rematch from post). */
  start(p: HostedStartPayload, now: number = Date.now()): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    this.m.startConfig = p.config ?? null;
    this.m.matchRoster = p.roster ?? [];
    this.m.matchEndsAt = p.config ? now + p.config.roundSeconds * 1000 : 0;
    this.m.standings = [];
    this.m.roundEndReason = "";
    this.m.startRejected = null;
    this.m.phase = "match";
  }

  /** `roundEnd {standings, reason, nextStartAt}` — into the post phase. */
  roundEnd(p: HostedRoundEndPayload): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    this.m.standings = p.standings ?? [];
    this.m.roundEndReason = p.reason ?? "";
    this.m.startConfig = null; // this round is over — never re-boot off it
    this.m.matchEndsAt = 0;
    this.m.phase = "post";
  }

  /** `lobbyStartRejected {reason, graceEndsAt}` — leader-only start gate. */
  startRejected(p: LobbyStartRejectedPayload): void {
    if (this.m.phase === "error" || this.m.phase === "rejected") return;
    this.m.startRejected = p;
  }

  error(message: string): void {
    this.m.phase = "error";
    this.m.errorMessage = message;
    this.m.inGame = false;
  }

  /**
   * Deferred spawn, client side: boot the local Game only when a round we are
   * seated in has started. In a hosted room everyone in the lobby at start IS
   * seated (the server seats the frozen roster), so no separate seat grant.
   */
  shouldEnterMatch(): boolean {
    return this.m.phase === "match" && this.m.startConfig !== null && !this.m.inGame;
  }

  markInGame(): void {
    this.m.inGame = true;
  }

  clearLeaderNotice(): void {
    this.m.leaderNotice = "";
  }

  clearStartRejected(): void {
    this.m.startRejected = null;
  }

  // ── Derived reads ─────────────────────────────────────────────────────────

  selfMember(): LobbyMemberPayload | null {
    return this.m.members.find((x) => x.sessionId === this.m.ownSessionId) ?? null;
  }

  selfReady(): boolean {
    return this.selfMember()?.ready === true;
  }

  allReady(): boolean {
    return this.m.members.length > 0 && this.m.members.every((x) => x.ready);
  }

  /** Client-side hint only — the server re-gates on `lobbyStart`. */
  looksStartable(): boolean {
    return this.m.members.length >= LOBBY_MIN_PLAYERS && this.allReady();
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private applyServerPhase(phase: HostedLobbyPhase): void {
    switch (phase) {
      case "lobby": this.m.phase = "lobby"; break;
      case "match": this.m.phase = "match"; break;
      case "post":  this.m.phase = "post";  break;
    }
  }

  private reflagLeader(sessionId: string | null): void {
    for (const member of this.m.members) member.isLeader = member.sessionId === sessionId;
  }
}

// ── Code entry (landing screen keypad) ───────────────────────────────────────

/**
 * Client copy of the server's unambiguous code alphabet — no O/0, no I/1
 * (`server/src/rooms/lobbyCodes.ts`, which the client cannot import).
 */
export const LOBBY_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const LOBBY_CODE_LENGTH = 5;

const KEYPAD_COLS = 8;
const KEYPAD_CHAR_ROWS = LOBBY_CODE_ALPHABET.length / KEYPAD_COLS; // 4

export type KeypadCell =
  | { kind: "char"; ch: string }
  | { kind: "del" }
  | { kind: "go" };

/** Uppercase and keep only alphabet characters, clipped to code length. */
export function sanitizeCode(raw: string): string {
  let out = "";
  for (const ch of String(raw).toUpperCase()) {
    if (LOBBY_CODE_ALPHABET.includes(ch)) out += ch;
    if (out.length >= LOBBY_CODE_LENGTH) break;
  }
  return out;
}

/**
 * The controller-navigable code keypad: a 4×8 grid of alphabet keys plus an
 * action row [DEL, GO]. Pure model — the screen renders `cells()` and drives
 * `move`/`activate` from MenuActions; keyboard typing goes through `setFrom`.
 *
 * Cursor indices: 0..31 = chars (row·8+col), 32 = DEL, 33 = GO.
 * Vertical movement wraps through the action row; DEL maps to the left half
 * of a char row (col 2), GO to the right half (col 5).
 */
export class CodeEntryModel {
  value = "";
  cursor = 0;

  cells(): KeypadCell[] {
    const out: KeypadCell[] = [];
    for (const ch of LOBBY_CODE_ALPHABET) out.push({ kind: "char", ch });
    out.push({ kind: "del" });
    out.push({ kind: "go" });
    return out;
  }

  get complete(): boolean {
    return this.value.length === LOBBY_CODE_LENGTH;
  }

  push(ch: string): void {
    const clean = sanitizeCode(ch).slice(0, 1);
    if (!clean) return;
    if (this.value.length >= LOBBY_CODE_LENGTH) return;
    this.value += clean;
  }

  pop(): void {
    this.value = this.value.slice(0, -1);
  }

  clear(): void {
    this.value = "";
  }

  setFrom(raw: string): void {
    this.value = sanitizeCode(raw);
  }

  /**
   * Activate the cell under the cursor. Chars/DEL mutate the value; the caller
   * acts on the returned kind (only "go" needs outside handling, and only when
   * the code is complete).
   */
  activate(): KeypadCell["kind"] {
    const cell = this.cells()[this.cursor]!;
    if (cell.kind === "char") this.push(cell.ch);
    else if (cell.kind === "del") this.pop();
    return cell.kind;
  }

  move(dir: "up" | "down" | "left" | "right"): void {
    const delIdx = LOBBY_CODE_ALPHABET.length;     // 32
    const goIdx = LOBBY_CODE_ALPHABET.length + 1;  // 33
    const onActions = this.cursor >= delIdx;

    if (dir === "left" || dir === "right") {
      const step = dir === "left" ? -1 : 1;
      if (onActions) {
        this.cursor = this.cursor === delIdx ? goIdx : delIdx; // 2-cell row wraps
      } else {
        const row = Math.floor(this.cursor / KEYPAD_COLS);
        const col = (this.cursor % KEYPAD_COLS + step + KEYPAD_COLS) % KEYPAD_COLS;
        this.cursor = row * KEYPAD_COLS + col;
      }
      return;
    }

    const step = dir === "up" ? -1 : 1;
    if (onActions) {
      // Leave the action row: DEL re-enters at col 2, GO at col 5.
      const col = this.cursor === delIdx ? 2 : 5;
      const row = dir === "down" ? 0 : KEYPAD_CHAR_ROWS - 1;
      this.cursor = row * KEYPAD_COLS + col;
      return;
    }
    const row = Math.floor(this.cursor / KEYPAD_COLS);
    const col = this.cursor % KEYPAD_COLS;
    const nextRow = row + step;
    if (nextRow < 0 || nextRow >= KEYPAD_CHAR_ROWS) {
      this.cursor = col < KEYPAD_COLS / 2 ? delIdx : goIdx; // into the action row
      return;
    }
    this.cursor = nextRow * KEYPAD_COLS + col;
  }
}

// ── Settings options (leader controls) ───────────────────────────────────────

export const MATCH_TYPE_OPTIONS: readonly HostedMatchType[] = [
  "deathmatch",
  "lms",
  "team-battle",
  "free-drive",
];

export const ROUND_SECONDS_OPTIONS: readonly number[] = [60, 120, 180, 240, 300, 600];
export const SCORE_LIMIT_OPTIONS: readonly number[] = [0, 10, 20, 30, 50];
export const MAX_PLAYERS_OPTIONS: readonly number[] = [2, 3, 4, 5, 6, 7, 8];

/**
 * Step through an option list. When `current` is not in the list (server env
 * overrides can produce e.g. roundSeconds=90), numbers snap to the closest
 * entry first, then step; non-members of non-numeric lists start at index 0.
 */
export function cycleOption<T extends number | string>(
  list: readonly T[],
  current: T,
  dir: 1 | -1,
): T {
  let idx = list.indexOf(current);
  if (idx === -1) {
    if (typeof current === "number") {
      let best = 0;
      let bestDist = Infinity;
      list.forEach((v, i) => {
        const d = Math.abs((v as number) - current);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      idx = best;
    } else {
      idx = 0;
    }
  }
  return list[(idx + dir + list.length) % list.length]!;
}

// ── Projections (pure) ───────────────────────────────────────────────────────

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

/** ms → "M:SS", clamped at zero. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round((Number.isFinite(ms) ? ms : 0) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Milliseconds left on a local-clock deadline. 0 when unset or elapsed. */
export function remainingMs(deadline: number, now: number = Date.now()): number {
  if (!deadline) return 0;
  return Math.max(0, deadline - now);
}

export function matchTypeLabel(t: HostedMatchType): string {
  switch (t) {
    case "deathmatch":  return "DEATHMATCH";
    case "lms":         return "LAST MAN STANDING";
    case "team-battle": return "TEAM BATTLE";
    case "free-drive":  return "FREE DRIVE";
  }
}

/** The shell covers everything except a round we are actually playing. */
export function shellVisibleFor(m: Readonly<LobbyModel>): boolean {
  return !(m.phase === "match" && m.inGame);
}

export function headerFor(m: Readonly<LobbyModel>): string {
  switch (m.phase) {
    case "idle":
    case "connecting": return m.mode === "host" ? "OPENING ROOM" : "JOINING ROOM";
    case "lobby":      return "HOSTED LOBBY";
    case "match":      return "IN MATCH";
    case "post":       return "ROUND OVER";
    case "rejected":   return "ROOM BUSY";
    case "error":      return "CONNECTION FAILED";
  }
}

export function statusLineFor(m: Readonly<LobbyModel>): string {
  switch (m.phase) {
    case "idle":
      return "";
    case "connecting":
      return m.mode === "host" ? "SETTING UP A PRIVATE ROOM…" : "LOOKING UP THE ROOM…";
    case "rejected":
    case "error":
      return m.errorMessage;
    case "lobby": {
      if (m.members.length < LOBBY_MIN_PLAYERS) {
        return `WAITING FOR PLAYERS — NEED AT LEAST ${LOBBY_MIN_PLAYERS}`;
      }
      const readyCount = m.members.filter((x) => x.ready).length;
      if (readyCount < m.members.length) {
        return `READY ${readyCount} OF ${m.members.length}${m.isLeader ? "" : " — LEADER STARTS THE MATCH"}`;
      }
      return m.isLeader ? "ALL READY — YOU CAN START" : "ALL READY — WAITING FOR THE LEADER";
    }
    case "match":
      return "MATCH IN PROGRESS";
    case "post":
      return m.isLeader ? "REMATCH WHEN YOU ARE — OR BACK TO THE LOBBY" : "LEADER PICKS THE REMATCH";
  }
}

export function startRejectedLine(p: LobbyStartRejectedPayload): string {
  return p.reason === "not-enough-players"
    ? `CAN'T START — NEED AT LEAST ${LOBBY_MIN_PLAYERS} PLAYERS`
    : "CAN'T START — NOT EVERYONE IS READY";
}

export function roundEndLine(reason: string): string {
  switch (reason) {
    case "time":        return "TIME UP";
    case "score-limit": return "SCORE LIMIT REACHED";
    case "win":         return "ROUND DECIDED";
    default:            return "ROUND OVER";
  }
}

export function settingsSummary(s: LobbySettingsPayload | null): string {
  if (!s) return "";
  const bits = [matchTypeLabel(s.matchType), formatClock(s.roundSeconds * 1000)];
  if (s.matchType === "deathmatch" && s.scoreLimit > 0) bits.push(`FIRST TO ${s.scoreLimit}`);
  bits.push(s.botFill ? "BOTS ON" : "BOTS OFF");
  bits.push(`MAX ${s.maxPlayers}`);
  return bits.join(" · ");
}

/** teamId → css suffix. 0 = unassigned/FFA, 1/2 = the two team colours. */
export function teamClass(team: number): string {
  return team === 1 ? "t1" : team === 2 ? "t2" : "t0";
}

/**
 * Roster panels, always `maxPlayers` slots — empty slots invite. `members`
 * arrive in server map order; leader flag and ready lamp come off each row.
 */
export function renderLobbyRoster(
  members: readonly LobbyMemberPayload[],
  ownSessionId: string,
  maxPlayers: number,
): string {
  const slots: string[] = [];
  const count = Math.max(members.length, Math.min(Math.max(maxPlayers, 1), 8));
  for (let i = 0; i < count; i++) {
    const member = members[i];
    if (!member) {
      slots.push(
        `<div class="lb-slot empty"><span class="lb-slot-name">AWAITING PLAYER</span><span class="lb-lamp off"></span></div>`,
      );
      continue;
    }
    const cls = [
      "lb-slot",
      teamClass(member.team),
      member.sessionId === ownSessionId ? "you" : "",
      member.isLeader ? "leader" : "",
    ].filter(Boolean).join(" ");
    const crown = member.isLeader ? `<span class="lb-crown" title="Leader">♛</span>` : "";
    const you = member.sessionId === ownSessionId ? `<span class="lb-you">YOU</span>` : "";
    const lamp = `<span class="lb-lamp ${member.ready ? "on" : "off"}"></span>`;
    slots.push(
      `<div class="${cls}">${crown}<span class="lb-slot-name">${escapeHtml(member.name)}</span>${you}` +
      `<span class="lb-ready-tag">${member.ready ? "READY" : "NOT READY"}</span>${lamp}</div>`,
    );
  }
  return slots.join("");
}

/** Post-round standings. Sorted + bot-free on arrival (ArenaRoom.buildStandings). */
export function renderLobbyStandings(
  standings: readonly RoundStandingView[],
  ownSessionId: string,
): string {
  if (standings.length === 0) {
    return `<div class="lb-standings-empty">NO STANDINGS — ROUND ENDED EARLY</div>`;
  }
  const head = `<div class="lb-row lb-head"><span>#</span><span>PLAYER</span><span>K</span><span>D</span></div>`;
  const rows = standings.map((s, i) => {
    const cls = ["lb-row", s.sessionId === ownSessionId ? "you" : "", i === 0 ? "winner" : ""]
      .filter(Boolean).join(" ");
    return `<div class="${cls}">` +
      `<span class="lb-rank">${i + 1}</span>` +
      `<span class="lb-name">${escapeHtml(s.name)}</span>` +
      `<span class="lb-kills">${s.score}</span>` +
      `<span class="lb-deaths">${s.deaths}</span>` +
      `</div>`;
  }).join("");
  return head + rows;
}

export function winnerNameOf(standings: readonly RoundStandingView[]): string {
  return standings.length > 0 ? standings[0]!.name : "";
}
